/**
 * 播放协调控制 composable
 * 协调 useAudioPlayback 和 useReadAlong 之间的交互逻辑
 * 包括 togglePlay、字幕导航、播放结束处理
 */
export function usePlaybackControl({
  sound,
  isPlaying,
  currentSubtitleIndex,
  currentTime,
  displaySubtitles,
  playCurrent,
  pauseCurrent,
  stopCurrent,
  seekCurrent,
  isCurrentPlaying,
  // readAlong 状态
  isReadAlongMode,
  readAlongMode,
  isRecording,
  isPaused,
  isWaitingForShadowing,
  isPausedWhileShadowing,
  waitingForRecording,
  // readAlong 方法
  stopRecording,
  clearAllTimers,
  setupPauseTimer,
  setupFollowingTimer,
  closeMediaStream,
  // 回调
  setOnPauseCallback,
  setOnEndedCallback,
  setPlayNextSubtitle,
  onPause,
  // 导航
  handleViewChange
}) {
  function togglePlay() {
    if (!sound.value) return
    if (isPlaying.value) {
      isPaused.value = true
      pauseCurrent()
      isPlaying.value = false
      if (isRecording.value) {
        stopRecording()
        waitingForRecording.value = false
      }
      clearAllTimers()
    } else {
      if (
        isReadAlongMode.value &&
        (
          (readAlongMode.value !== 'following' && waitingForRecording.value) ||
          (readAlongMode.value === 'following' && (isWaitingForShadowing.value || isPausedWhileShadowing.value))
        )
      ) {
        playSubtitleAtIndex(currentSubtitleIndex.value)
        return
      }

      // 等待跟读状态 → 暂停
      if (isReadAlongMode.value && readAlongMode.value === 'following' && isWaitingForShadowing.value && !isPausedWhileShadowing.value) {
        isWaitingForShadowing.value = false
        isPaused.value = true
        isPausedWhileShadowing.value = true
        return
      }

      // 已暂停的等待跟读状态 → 恢复
      if (isReadAlongMode.value && readAlongMode.value === 'following' && isPausedWhileShadowing.value) {
        isPausedWhileShadowing.value = false
        isWaitingForShadowing.value = false
        isPaused.value = false
        isPlaying.value = true
        if (currentSubtitleIndex.value !== null) {
          const subtitle = displaySubtitles.value[currentSubtitleIndex.value]
          if (subtitle) {
            seekCurrent(subtitle.start)
            playCurrent()
            setupFollowingTimer()
          }
        }
        return
      }

      isPaused.value = false
      isPlaying.value = true
      playCurrent()

      if (isReadAlongMode.value && readAlongMode.value !== 'following') {
        setupPauseTimer()
      } else if (isReadAlongMode.value && readAlongMode.value === 'following') {
        setupFollowingTimer()
      }
    }
  }

  function playSubtitleAtIndex(index) {
    if (!sound.value || index === null || index < 0 || index >= displaySubtitles.value.length) return

    if (isRecording.value || waitingForRecording.value) {
      stopRecording()
    }
    clearAllTimers()

    const subtitle = displaySubtitles.value[index]
    if (!subtitle) return

    currentSubtitleIndex.value = index

    if (isReadAlongMode.value && readAlongMode.value === 'following' && isWaitingForShadowing.value) {
      isWaitingForShadowing.value = false
    }
    if (isReadAlongMode.value && readAlongMode.value === 'following' && isPausedWhileShadowing.value) {
      isPausedWhileShadowing.value = false
      isWaitingForShadowing.value = false
    }

    // Howler 会在连续 play() 时创建新的播放实例。
    // 快速切句时先暂停当前实例，再重设时间并恢复，避免同一音频重叠播放。
    if (isCurrentPlaying()) {
      pauseCurrent({ suppressCallback: true })
    }

    seekCurrent(subtitle.start)
    isPlaying.value = true
    isPaused.value = false
    waitingForRecording.value = false

    playCurrent()

    if (isReadAlongMode.value && readAlongMode.value !== 'following') {
      setupPauseTimer()
    } else if (isReadAlongMode.value && readAlongMode.value === 'following') {
      setupFollowingTimer()
    }
  }

  function playPreviousSubtitle() {
    if (currentSubtitleIndex.value > 0) {
      playSubtitleAtIndex(currentSubtitleIndex.value - 1)
    }
  }

  function playNextSubtitle(fromIndex = currentSubtitleIndex.value) {
    if (!displaySubtitles.value || currentSubtitleIndex.value === null) return
    const baseIndex = Number.isInteger(fromIndex) ? fromIndex : currentSubtitleIndex.value
    const nextIndex = baseIndex + 1
    if (nextIndex >= displaySubtitles.value.length) {
      clearAllTimers()
      isWaitingForShadowing.value = false
      isPlaying.value = false
      if (sound.value) {
        pauseCurrent()
      }
      return
    }
    playSubtitleAtIndex(nextIndex)
  }

  function onEnded() {
    if (isReadAlongMode.value) {
      isPlaying.value = false
      waitingForRecording.value = false
      return
    }
  }

  // 导航前的清理
  function goToView(view, params) {
    if (sound.value) {
      stopCurrent()
      isPlaying.value = false
    }
    if (isRecording.value) {
      stopRecording()
    }
    waitingForRecording.value = false
    isReadAlongMode.value = false
    readAlongMode.value = null
    isRecording.value = false
    closeMediaStream()
    currentSubtitleIndex.value = null
    currentTime.value = 0
    clearAllTimers()
    handleViewChange(view, params)
  }

  // 注入回调
  function injectCallbacks() {
    setPlayNextSubtitle(playNextSubtitle)
    setOnPauseCallback(onPause)
    setOnEndedCallback(onEnded)
  }

  return {
    togglePlay,
    playSubtitleAtIndex,
    playPreviousSubtitle,
    playNextSubtitle,
    onEnded,
    goToView,
    injectCallbacks
  }
}
